import React, { useEffect, useState } from 'react';
import StockAlertBar from './StockAlertBar';

const WHATSAPP_URL = 'https://wa.me/447716089293';

// Root-relative links so the same nav works from every route.
const navItems = [
    { href: '/#aboutourcoffee', label: 'Our Coffee' },
    { href: '/about', label: 'About' },
    { href: '/#howtoorder', label: 'How to Order' },
    { href: '/wholesale', label: 'Wholesale' },
    { href: '/#faq', label: 'FAQ' },
    { href: '/contact', label: 'Contact' },
];

// `overlay` starts transparent over a dark hero and turns solid oat once the page scrolls.
export default function SiteHeader({ overlay = false, current }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        if (!overlay) return;
        const onScroll = () => setIsScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [overlay]);

    useEffect(() => {
        if (!isMenuOpen) return;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setIsMenuOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isMenuOpen]);

    const transparent = overlay && !isScrolled;

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-40">
                <StockAlertBar />
                <nav
                    aria-label="Primary"
                    className={`transition-colors duration-300 ${transparent ? 'bg-transparent border-b border-transparent' : 'bg-oat/95 backdrop-blur border-b border-gray-200'}`}
                >
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="max-w-6xl mx-auto flex items-center justify-between h-16 lg:h-20">
                            <a href="/" className="inline-block">
                                <img
                                    className="h-12 lg:h-14 w-auto"
                                    src={transparent ? '/flow-assets/logos/mubarak-farm-logo-white.png' : '/flow-assets/logos/mubarak-logo.png'}
                                    alt="Mubarak Farm"
                                    width={480}
                                    height={199}
                                    fetchpriority="high"
                                />
                            </a>

                            <ul className="hidden lg:flex items-center gap-7">
                                {navItems.map((item) => (
                                    <li key={item.href}>
                                        <a
                                            href={item.href}
                                            aria-current={current === item.href ? 'page' : undefined}
                                            className={`text-sm font-medium transition-colors ${
                                                current === item.href
                                                    ? (transparent ? 'text-gold-400' : 'text-clay')
                                                    : (transparent ? 'text-white/80 hover:text-gold-400' : 'text-gray-700 hover:text-clay')
                                            }`}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex items-center gap-3">
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hidden sm:inline-flex items-center bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-5 py-2.5 rounded-full text-sm transition-[background-color,transform] duration-200 active:scale-[0.97]"
                                >
                                    Order Now
                                </a>
                                <button
                                    className={`lg:hidden p-1 transition-colors ${transparent ? 'text-white hover:text-gold-400' : 'text-gray-900 hover:text-clay'}`}
                                    onClick={() => setIsMenuOpen(true)}
                                    aria-label="Open navigation menu"
                                    aria-expanded={isMenuOpen}
                                >
                                    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile menu */}
            <div className={isMenuOpen ? 'block' : 'hidden'}>
                <div className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
                <nav className="fixed top-0 right-0 bottom-0 z-50 flex flex-col w-full xs:w-4/5 xs:max-w-sm bg-gray-900 border-l border-gray-800 overflow-y-auto" aria-label="Mobile">
                    <div className="flex items-center justify-between p-6 border-b border-gray-800">
                        <a href="/" className="inline-block" onClick={() => setIsMenuOpen(false)}>
                            <img className="h-12 w-auto" src="/flow-assets/logos/mubarak-farm-logo-white.png" alt="Mubarak Farm" width={480} height={199} />
                        </a>
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                            aria-label="Close navigation menu"
                        >
                            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <ul className="flex-1 py-6 px-6 divide-y divide-gray-800">
                        {navItems.map((item) => (
                            <li key={item.href}>
                                <a
                                    className={`block py-4 font-heading text-2xl transition-colors ${current === item.href ? 'text-gold-400' : 'text-gray-100 hover:text-gold-400'}`}
                                    href={item.href}
                                    aria-current={current === item.href ? 'page' : undefined}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="p-6 border-t border-gray-800">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center justify-center w-full py-4 px-6 text-base font-bold text-gray-900 bg-gold-400 hover:bg-gold-300 rounded-full transition-colors active:scale-[0.97]"
                        >
                            <span>Order via WhatsApp</span>
                            <span className="text-xs font-semibold mt-0.5 text-gray-800">Pay Cash on Delivery</span>
                        </a>
                    </div>
                </nav>
            </div>
        </>
    );
}
